import { formatCurrency } from '../../helper'

// Mirrors i-quote-server's src/distributors/tdsynnex-sftp.ts TdSynnexWarehouse/TdSynnexGovClass/TdSynnexSftpItemV2.
// Keep in sync manually — the two repos don't share types.

export type TdSynnexWarehouse = {
  location: string
  code: string
  qty: number
}

export type TdSynnexGovClass = {
  raw: string
  coo: string | null
  energyStar: string
  section508: boolean
  warrantyPeriod: string | null
  epeatLevel: string | null
  caProp65: boolean
}

export type TdSynnexSftpItemV2 = {
  mfgPN: string
  synnexPartNo: string
  synnexSKU: string
  statusCode: string
  abcCode: string | null
  kitFlag: string | null
  categoryCode: string | null
  skuCreatedDate: string | null
  mediaType: string | null
  platformType: string | null
  streetDate: string | null

  shortDescription: string
  longDescription1: string | null
  longDescription2: string | null
  longDescription3: string | null
  manufacturer: string

  contractPrice: number | null
  unitCostWithoutPromo: number | null
  msrp: number | null
  mapPrice: number | null
  healthcarePrice: number | null

  promoFlag: boolean
  promoComment: string | null
  promoExpiryDate: string | null

  taaCompliant: string
  cooList: string | null
  stateGovPrice: number | null
  federalGovPrice: number | null
  educationalPrice: number | null
  gsaPrice: number | null
  gsaNtePrice: number | null

  govClass: TdSynnexGovClass | null

  qtyOnHand: number
  mfgDropShipQty: number
  etaDate: string | null
  warehouses: TdSynnexWarehouse[]

  minOrderQty: number | null
  replacementSku: string | null
  endUserInfoRequired: boolean
  isVirtualProduct: boolean
  dropShipOnly: boolean
  isRefurbished: boolean
  mfgUpcAvailable: boolean

  shipWeight: number | null
  length: number | null
  width: number | null
  height: number | null

  upcCode: string | null
  unspscCode: string | null

  returnable: boolean
  parcelShippable: boolean
  serialized: boolean
}

export const TD_SYNNEX_SECTIONS = [
  'Identity',
  'Descriptions',
  'Pricing',
  'Promotions',
  'Government Pricing',
  'Availability',
  'Order / Purchasing',
  'Physical',
  'Codes',
  'Flags',
] as const

export type TdSynnexSection = (typeof TD_SYNNEX_SECTIONS)[number]

export type TdSynnexFieldRow = {
  fieldNumber: string
  name: string
  description: string
  section: TdSynnexSection
  accessor: (item: TdSynnexSftpItemV2) => string
}

const dash = '—'
export const formatBoolean = (v: boolean) => (v ? 'Yes' : 'No')
const text = (v: string | null | undefined) => v || dash
const num = (v: number | null | undefined) => (v === null || v === undefined ? dash : String(v))
const currency = (v: number | null | undefined) => (v === null || v === undefined ? dash : formatCurrency(v))
const warehousesSummary = (w: TdSynnexWarehouse[]) =>
  w.length ? w.map(x => `${x.location}: ${x.qty}`).join(', ') : dash
const govClassSummary = (g: TdSynnexGovClass | null) =>
  g
    ? `COO: ${text(g.coo)} | Energy Star: ${text(g.energyStar)} | Section 508: ${formatBoolean(
        g.section508,
      )} | Warranty: ${text(g.warrantyPeriod)} | EPEAT: ${text(g.epeatLevel)} | CA Prop 65: ${formatBoolean(
        g.caProp65,
      )}`
    : dash

export const TD_SYNNEX_FIELD_ROWS: TdSynnexFieldRow[] = [
  // Identity
  {
    fieldNumber: '03',
    name: 'Manufacturer Part #',
    description: "Manufacturer's Part Number",
    section: 'Identity',
    accessor: item => text(item.mfgPN),
  },
  {
    fieldNumber: '04',
    name: 'TD Synnex Part #',
    description: 'TD Synnex Part Number (Alpha numeric part #)',
    section: 'Identity',
    accessor: item => text(item.synnexPartNo),
  },
  {
    fieldNumber: '05',
    name: 'Synnex SKU #',
    description: 'Unique TD SYNNEX Sku Number',
    section: 'Identity',
    accessor: item => text(item.synnexSKU),
  },
  {
    fieldNumber: '06',
    name: 'Status Code',
    description: 'A = Active, C/S = Changed, D = Deleted/Discontinued',
    section: 'Identity',
    accessor: item => text(item.statusCode),
  },
  {
    fieldNumber: '08',
    name: 'Manufacturer Name',
    description: "Manufacturer's Name",
    section: 'Identity',
    accessor: item => text(item.manufacturer),
  },
  {
    fieldNumber: '23',
    name: 'Media Type',
    description: 'RTL = Retail, SWL = SW License, SWMP = SW Physical',
    section: 'Identity',
    accessor: item => text(item.mediaType),
  },
  {
    fieldNumber: '25',
    name: 'Category Code',
    description: 'TD SYNNEX Product Categorization Code',
    section: 'Identity',
    accessor: item => text(item.categoryCode),
  },
  {
    fieldNumber: '37',
    name: 'SKU Created Date',
    description: 'Date this SKU was created in TD SYNNEX',
    section: 'Identity',
    accessor: item => text(item.skuCreatedDate),
  },
  {
    fieldNumber: '40',
    name: 'ABC Code',
    description: 'A=Active, B=Special Order, C=EOL, T=To be Discont',
    section: 'Identity',
    accessor: item => text(item.abcCode),
  },
  {
    fieldNumber: '41',
    name: 'Kit/Stand Alone Flag',
    description: 'K=Kit SKU, S=Stand Alone',
    section: 'Identity',
    accessor: item => text(item.kitFlag),
  },
  {
    fieldNumber: '58',
    name: 'Platform Type',
    description: 'ALL, LINUX, MAC, PC, SUN, UNIX',
    section: 'Identity',
    accessor: item => text(item.platformType),
  },
  {
    fieldNumber: '60',
    name: 'Street Date',
    description: 'Manufacturer public release date',
    section: 'Identity',
    accessor: item => text(item.streetDate),
  },
  {
    fieldNumber: '65',
    name: 'Replacement SKU',
    description: 'Replacement SKU for discontinued product',
    section: 'Identity',
    accessor: item => text(item.replacementSku),
  },

  // Descriptions
  {
    fieldNumber: '07',
    name: 'Short Description',
    description: 'Product Description (short)',
    section: 'Descriptions',
    accessor: item => text(item.shortDescription),
  },
  {
    fieldNumber: '50',
    name: 'Long Description 1',
    description: 'Extended product description (available upon request)',
    section: 'Descriptions',
    accessor: item => text(item.longDescription1),
  },
  {
    fieldNumber: '51',
    name: 'Long Description 2',
    description: 'Extended product description (available upon request)',
    section: 'Descriptions',
    accessor: item => text(item.longDescription2),
  },
  {
    fieldNumber: '52',
    name: 'Long Description 3',
    description: 'Extended product description (available upon request)',
    section: 'Descriptions',
    accessor: item => text(item.longDescription3),
  },

  // Pricing
  {
    fieldNumber: '13',
    name: 'Contract Price',
    description: 'Contract price including active promo/rebate',
    section: 'Pricing',
    accessor: item => currency(item.contractPrice),
  },
  {
    fieldNumber: '21',
    name: 'Unit Cost (No Promo)',
    description: 'Unit cost without promo/rebate applied',
    section: 'Pricing',
    accessor: item => currency(item.unitCostWithoutPromo),
  },
  {
    fieldNumber: '14',
    name: 'MSRP',
    description: "Manufacturer's suggested retail price",
    section: 'Pricing',
    accessor: item => currency(item.msrp),
  },
  {
    fieldNumber: '31',
    name: 'MAP Price',
    description: 'Minimum advertised price',
    section: 'Pricing',
    accessor: item => currency(item.mapPrice),
  },
  {
    fieldNumber: '33',
    name: 'Healthcare Price',
    description: 'Healthcare-tier pricing',
    section: 'Pricing',
    accessor: item => currency(item.healthcarePrice),
  },

  // Promotions
  {
    fieldNumber: '47',
    name: 'Promo Flag',
    description: 'Whether an active promo/rebate applies',
    section: 'Promotions',
    accessor: item => formatBoolean(item.promoFlag),
  },
  {
    fieldNumber: '48',
    name: 'Promo Comment',
    description: 'Promo/rebate details',
    section: 'Promotions',
    accessor: item => text(item.promoComment),
  },
  {
    fieldNumber: '49',
    name: 'Promo Expiry Date',
    description: 'Date the current promo/rebate expires',
    section: 'Promotions',
    accessor: item => text(item.promoExpiryDate),
  },

  // Government Pricing
  {
    fieldNumber: '45',
    name: 'TAA Compliant',
    description: 'Trade Agreements Act compliance flag',
    section: 'Government Pricing',
    accessor: item => text(item.taaCompliant),
  },
  {
    fieldNumber: '32',
    name: 'Country of Origin List',
    description: 'List of possible countries of origin',
    section: 'Government Pricing',
    accessor: item => text(item.cooList),
  },
  {
    fieldNumber: '42',
    name: 'State Gov Price',
    description: 'State government pricing tier',
    section: 'Government Pricing',
    accessor: item => currency(item.stateGovPrice),
  },
  {
    fieldNumber: '43',
    name: 'Federal Gov Price',
    description: 'Federal government pricing tier',
    section: 'Government Pricing',
    accessor: item => currency(item.federalGovPrice),
  },
  {
    fieldNumber: '44',
    name: 'Educational Price',
    description: 'Educational institution pricing tier',
    section: 'Government Pricing',
    accessor: item => currency(item.educationalPrice),
  },
  {
    fieldNumber: '46',
    name: 'GSA Price',
    description: 'GSA schedule pricing',
    section: 'Government Pricing',
    accessor: item => currency(item.gsaPrice),
  },
  {
    fieldNumber: '57',
    name: 'GSA NTE Price',
    description: 'GSA "not to exceed" price',
    section: 'Government Pricing',
    accessor: item => currency(item.gsaNtePrice),
  },
  {
    fieldNumber: '68',
    name: 'Gov Classification',
    description: 'COO, Energy Star, Section 508, warranty, EPEAT, CA Prop 65',
    section: 'Government Pricing',
    accessor: item => govClassSummary(item.govClass),
  },

  // Availability
  {
    fieldNumber: '10',
    name: 'Qty On Hand',
    description: 'Total quantity on hand across all warehouses',
    section: 'Availability',
    accessor: item => num(item.qtyOnHand),
  },
  {
    fieldNumber: '70',
    name: 'Mfg Drop Ship Qty',
    description: 'Manufacturer direct drop-ship quantity',
    section: 'Availability',
    accessor: item => num(item.mfgDropShipQty),
  },
  {
    fieldNumber: '39',
    name: 'ETA Date',
    description: 'Estimated availability date when out of stock',
    section: 'Availability',
    accessor: item => text(item.etaDate),
  },
  {
    fieldNumber: '15-69',
    name: 'Warehouse Breakdown',
    description: 'Per-location quantity on hand',
    section: 'Availability',
    accessor: item => warehousesSummary(item.warehouses),
  },

  // Order / Purchasing
  {
    fieldNumber: '66',
    name: 'Min Order Qty',
    description: 'Minimum order quantity',
    section: 'Order / Purchasing',
    accessor: item => num(item.minOrderQty),
  },
  {
    fieldNumber: '67 (char 0)',
    name: 'End User Info Required',
    description: 'Whether end-user info must be collected at order time',
    section: 'Order / Purchasing',
    accessor: item => formatBoolean(item.endUserInfoRequired),
  },
  {
    fieldNumber: '67 (char 1)',
    name: 'Virtual/SW License Product',
    description: 'Whether this is a virtual/software license product',
    section: 'Order / Purchasing',
    accessor: item => formatBoolean(item.isVirtualProduct),
  },
  {
    fieldNumber: '38 (char 0)',
    name: 'Drop Ship Only',
    description: 'Whether this SKU can only be drop-shipped',
    section: 'Order / Purchasing',
    accessor: item => formatBoolean(item.dropShipOnly),
  },
  {
    fieldNumber: '38 (char 1)',
    name: 'Refurbished',
    description: 'Whether this SKU is refurbished',
    section: 'Order / Purchasing',
    accessor: item => formatBoolean(item.isRefurbished),
  },
  {
    fieldNumber: '38 (char 2)',
    name: 'Mfg UPC Available',
    description: 'Whether the manufacturer UPC is available',
    section: 'Order / Purchasing',
    accessor: item => formatBoolean(item.mfgUpcAvailable),
  },

  // Physical
  {
    fieldNumber: '28',
    name: 'Ship Weight',
    description: 'Shipping weight',
    section: 'Physical',
    accessor: item => num(item.shipWeight),
  },
  {
    fieldNumber: '53',
    name: 'Length',
    description: 'Product length',
    section: 'Physical',
    accessor: item => num(item.length),
  },
  {
    fieldNumber: '54',
    name: 'Width',
    description: 'Product width',
    section: 'Physical',
    accessor: item => num(item.width),
  },
  {
    fieldNumber: '55',
    name: 'Height',
    description: 'Product height',
    section: 'Physical',
    accessor: item => num(item.height),
  },

  // Codes
  {
    fieldNumber: '34',
    name: 'UPC Code',
    description: 'Universal Product Code',
    section: 'Codes',
    accessor: item => text(item.upcCode),
  },
  {
    fieldNumber: '35',
    name: 'UNSPSC Code',
    description: 'UN Standard Products and Services Code',
    section: 'Codes',
    accessor: item => text(item.unspscCode),
  },

  // Flags
  {
    fieldNumber: '17',
    name: 'Returnable',
    description: 'Whether this SKU is returnable',
    section: 'Flags',
    accessor: item => formatBoolean(item.returnable),
  },
  {
    fieldNumber: '19',
    name: 'Parcel Shippable',
    description: 'Whether this SKU can ship via parcel carrier',
    section: 'Flags',
    accessor: item => formatBoolean(item.parcelShippable),
  },
  {
    fieldNumber: '29',
    name: 'Serialized',
    description: 'Whether this SKU is serialized/tracked',
    section: 'Flags',
    accessor: item => formatBoolean(item.serialized),
  },
]
